/**
 * The two filters every observability screen shares (DL-245 item 4): a time
 * range and an environment. One filter row above everything it scopes, the
 * same slice for every chart under it (dataviz: "never per-chart filters").
 *
 * `TimeRangePicker` emits absolute `since`/`until` ISO instants rather than
 * a preset name, because every query the API accepts takes instants, and an
 * instant fixed when the reader chose it keeps a query key still across
 * re-renders. "Custom" is two `datetime-local` inputs, validated here
 * (since before until, at most 31 days — the range query's own bound)
 * before anything is sent.
 */

import { useId, useState, type SyntheticEvent } from 'react';

import { cx } from '~/lib/cx';

import { Button } from './Button';
import { Select } from './Choice';
import { Field, Input } from './Form';
import styles from './Filters.module.css';

export type TimeRangePreset = '1h' | '6h' | '24h' | '7d' | '28d' | '30d' | 'custom';

export interface TimeRange {
  preset: TimeRangePreset;
  /** RFC 3339. */
  since: string;
  /** RFC 3339. */
  until: string;
}

const PRESET_SECONDS: Record<Exclude<TimeRangePreset, 'custom'>, number> = {
  '1h': 3_600,
  '6h': 6 * 3_600,
  '24h': 24 * 3_600,
  '7d': 7 * 86_400,
  '28d': 28 * 86_400,
  '30d': 30 * 86_400,
};

export const TIME_RANGE_PRESETS: { value: TimeRangePreset; label: string }[] = [
  { value: '1h', label: 'Last hour' },
  { value: '6h', label: 'Last 6 hours' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '28d', label: 'Last 28 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom…' },
];

/** A range for a preset, ending now (or at `now`, for a test). */
export function presetRange(
  preset: Exclude<TimeRangePreset, 'custom'>,
  now: Date = new Date(),
): TimeRange {
  const until = new Date(now.getTime());
  const since = new Date(until.getTime() - PRESET_SECONDS[preset] * 1000);
  return { preset, since: since.toISOString(), until: until.toISOString() };
}

/** The seconds a range spans. */
export function rangeSeconds(range: TimeRange): number {
  return Math.max(0, (Date.parse(range.until) - Date.parse(range.since)) / 1000);
}

const MAX_CUSTOM_SECONDS = 31 * 86_400;

/** Validate a custom pair as the range query would. Returns a message or null. */
export function customRangeProblem(since: string, until: string): string | null {
  const s = Date.parse(since);
  const u = Date.parse(until);
  if (Number.isNaN(s) || Number.isNaN(u)) return 'Both times are needed.';
  if (s >= u) return 'The start must be before the end.';
  if (u - s > MAX_CUSTOM_SECONDS * 1000) return 'A range is at most 31 days.';
  if (u > Date.now() + 60_000) return 'The end cannot be in the future.';
  return null;
}

/** `datetime-local` wants `YYYY-MM-DDTHH:mm` in local time. */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export interface TimeRangePickerProps {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
  /** Presets to offer; the default is all of them. */
  presets?: TimeRangePreset[];
  className?: string;
}

export function TimeRangePicker({ value, onChange, presets, className }: TimeRangePickerProps) {
  const [customSince, setCustomSince] = useState(() => toLocalInput(value.since));
  const [customUntil, setCustomUntil] = useState(() => toLocalInput(value.until));
  const [problem, setProblem] = useState<string | null>(null);
  const formId = useId();
  const options = TIME_RANGE_PRESETS.filter((p) => !presets || presets.includes(p.value));

  const applyCustom = (e: SyntheticEvent) => {
    e.preventDefault();
    const since = customSince ? new Date(customSince).toISOString() : '';
    const until = customUntil ? new Date(customUntil).toISOString() : '';
    const why = customRangeProblem(since, until);
    setProblem(why);
    if (why) return;
    onChange({ preset: 'custom', since, until });
  };

  return (
    <div className={cx(styles.timeRange, className)}>
      <Select
        aria-label="Time range"
        value={value.preset}
        onValueChange={(v) => {
          const preset = v as TimeRangePreset;
          if (preset === 'custom') {
            setCustomSince(toLocalInput(value.since));
            setCustomUntil(toLocalInput(value.until));
            onChange({ ...value, preset: 'custom' });
            return;
          }
          setProblem(null);
          onChange(presetRange(preset));
        }}
        options={options}
      />
      {value.preset === 'custom' ? (
        <form
          className={styles.custom}
          onSubmit={applyCustom}
          aria-label="Custom time range"
          id={formId}
        >
          <Field label="From" error={problem}>
            <Input
              type="datetime-local"
              value={customSince}
              onChange={(e) => {
                setCustomSince(e.target.value);
              }}
              required
            />
          </Field>
          <Field label="To">
            <Input
              type="datetime-local"
              value={customUntil}
              onChange={(e) => {
                setCustomUntil(e.target.value);
              }}
              required
            />
          </Field>
          <Button type="submit" variant="secondary" size="sm">
            Apply range
          </Button>
        </form>
      ) : null}
    </div>
  );
}

export interface EnvironmentFilterProps {
  /** The environment names to offer, as the API spelt them. */
  environments: readonly string[];
  /** `undefined` means every environment. */
  value: string | undefined;
  onChange: (environment: string | undefined) => void;
  /** Offer "All environments". Off when a query needs exactly one. */
  allowAll?: boolean;
  label?: string;
  disabled?: boolean;
  className?: string;
}

const ALL = '__all__';

export function EnvironmentFilter({
  environments,
  value,
  onChange,
  allowAll = true,
  label = 'Environment',
  disabled = false,
  className,
}: EnvironmentFilterProps) {
  const options = [
    ...(allowAll ? [{ value: ALL, label: 'All environments' }] : []),
    ...environments.map((name) => ({ value: name, label: name })),
  ];
  const selected = value ?? (allowAll ? ALL : undefined);
  return (
    <Select
      aria-label={label}
      {...(className ? { className } : {})}
      disabled={disabled || options.length === 0}
      placeholder={options.length === 0 ? 'No environments' : 'Environment'}
      {...(selected === undefined ? {} : { value: selected })}
      onValueChange={(v) => {
        onChange(v === ALL ? undefined : v);
      }}
      options={options}
    />
  );
}
